"use client";

import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { type ReactNode, useLayoutEffect } from "react";
import { useAppThemeActions } from "@/context-api/app-theme-provider";
import { applyThemeToDocument } from "@/lib/themes/apply-theme";
import { findThemeByName } from "@/lib/themes/themes";

const isAppRoute = (pathname: string): boolean =>
  pathname === "/app" || pathname.startsWith("/app/");

const DEFAULT_THEME_NAME = "zinc-light";
const DEFAULT_DARK_THEME_NAME = "zinc-dark";

const getPublicThemeName = (): string => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_NAME;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? DEFAULT_DARK_THEME_NAME
    : DEFAULT_THEME_NAME;
};

/**
 * Routes outside `/app` always use Zinc (light or dark from OS preference).
 * Does not read or write the saved app theme in localStorage — visual only while active.
 * `/app` and `/app/*` use `AppThemeProvider` + localStorage as usual.
 */
const ThemeLockBridge = ({ children }: { children: ReactNode }) => {
  const { initTheme, setMarketingDocumentLock } = useAppThemeActions();
  const { setTheme: setNextTheme } = useTheme();

  useLayoutEffect(() => {
    setMarketingDocumentLock(true);

    const applyPublicTheme = () => {
      const name = getPublicThemeName();
      const theme = findThemeByName(name);
      applyThemeToDocument(theme);
      setNextTheme(theme.isDark ? "dark" : "light");
    };

    applyPublicTheme();

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", applyPublicTheme);

    return () => {
      mq.removeEventListener("change", applyPublicTheme);
      setMarketingDocumentLock(false);
      initTheme();
    };
  }, [initTheme, setMarketingDocumentLock, setNextTheme]);

  return <>{children}</>;
};

export const PublicThemeShell = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();

  if (isAppRoute(pathname)) {
    return <>{children}</>;
  }

  return <ThemeLockBridge>{children}</ThemeLockBridge>;
};
